import { Application } from 'pixi.js';
import { WordGame } from './game/WordGame';
import { loadLocale, t } from './i18n';
import { Keyboard } from './ui/Keyboard';
import { showResultModal } from './ui/ResultModal';
import { recordResult, epochDay } from './game/stats';
import { createPlatform, type Platform } from './platform';

async function boot() {
  await loadLocale('ru');
  document.title = t('title');

  // ---------- Платформа ----------
  const platform: Platform = await createPlatform();
  console.log(`[wdv] платформа: ${platform.id}`);

  // ---------- Pixi ----------
  const root = document.getElementById('pixi-root') as HTMLDivElement;
  const app = new Application();
  await app.init({
    background: 0xf4efe6,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio, 2),
    autoDensity: true,
    resizeTo: root
  });
  root.appendChild(app.canvas);

  const game = new WordGame(app);
  await game.init();

  // Счётчик завершённых игр для показа interstitial
  let gamesFinished = 0;

  game.onGameEnd(async (result) => {
    // Сохраняем результат через платформу
    const stats = recordResult(result.win, result.attempts, epochDay());
    await platform.saveStats(stats);

    // Отправляем в лидерборд (если есть)
    if (result.win) {
      await platform.submitScore(result.attempts).catch(() => {});
    }

    // Interstitial каждые 3 игры
    gamesFinished++;
    if (gamesFinished % 3 === 0) {
      await platform.showInterstitial().catch(() => {});
    }

    showResultModal({
      win: result.win,
      answer: result.answer,
      attempts: result.attempts,
      stats,
      onShare: () => { /* TODO: поделиться результатом */ },
      onPractice: () => { /* TODO: режим практики */ }
    });
  });

  // ---------- Клавиатура ----------
  const kbdRoot = document.getElementById('keyboard') as HTMLDivElement;
  const kbd = new Keyboard(kbdRoot);
  kbd.render('ru');
  kbd.on((e) => {
    if (e.type === 'letter') game.input(e.value);
    else if (e.type === 'enter') game.submit();
    else if (e.type === 'delete') game.backspace();
  });
  game.onLetterScored((letter, state) => kbd.setLetterState(letter, state));

  window.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') game.submit();
    else if (ev.key === 'Backspace') game.backspace();
    else if (ev.key.length === 1 && /[a-zа-яё]/i.test(ev.key)) game.input(ev.key.toUpperCase());
  });

  // ---------- Метка билда ----------
  const buildBadge = document.createElement('div');
  buildBadge.textContent = 'build ' + (window as any).__WDV_BUILD__;
  buildBadge.style.cssText = "font-family:'JetBrains Mono',monospace;font-size:9px;color:#9a9388;text-align:center;margin-top:4px;letter-spacing:.1em;";
  document.querySelector('header')?.appendChild(buildBadge);

  // ResizeObserver для перерисовки Pixi при изменении размера
  try {
    const ro = new ResizeObserver(() => { (app as any).resize?.(); });
    ro.observe(root);
  } catch {}

  // ---------- Кошелёк (только для web3-платформы) ----------
  if (platform.id === 'web') {
    const {
      initWallet, onWalletChange, ensureBase,
      openConnectModal, disconnect, formatAddress, BASE_CHAIN_ID,
      getWalletDiagnostics
    } = await import('./wallet');

    (window as any).__wdvWallet = { getWalletDiagnostics };

    const connectBtn = document.getElementById('connect-btn') as HTMLButtonElement;
    const networkPill = document.getElementById('network-pill') as HTMLDivElement;

    await initWallet();

    const render = () => {
      const { address, chainId, isConnected } = onWalletChange.state;
      if (!isConnected || !address) {
        connectBtn.textContent = 'connect wallet';
        connectBtn.classList.remove('connected');
        networkPill.textContent = 'not connected';
        networkPill.className = '';
        return;
      }
      connectBtn.textContent = formatAddress(address);
      connectBtn.classList.add('connected');
      if (chainId === BASE_CHAIN_ID) {
        networkPill.textContent = '● base';
        networkPill.className = 'base';
      } else {
        networkPill.textContent = '⚠ switch to base';
        networkPill.className = 'wrong';
      }
    };

    onWalletChange.subscribe(render);
    render();

    onWalletChange.subscribe(() => {
      const { isConnected, chainId } = onWalletChange.state;
      if (isConnected && chainId !== null && chainId !== BASE_CHAIN_ID) {
        ensureBase().catch(() => {});
      }
    });

    connectBtn.addEventListener('click', async () => {
      if (onWalletChange.state.isConnected) { await disconnect(); return; }
      await openConnectModal();
    });

    networkPill.addEventListener('click', async () => {
      if (networkPill.classList.contains('wrong')) await ensureBase();
    });
  } else {
    // Яндекс-версия: прячем wallet-элементы
    document.getElementById('connect-btn')?.remove();
    document.getElementById('network-pill')?.remove();
  }

  // ---------- Lifecycle ----------
  platform.onPause(() => {
    // TODO: пауза звуков/анимаций
  });
  platform.onResume(() => {
    // TODO: возобновление
  });
}

boot().catch((e) => console.error('[boot] fatal', e));
